import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, User } from '@isophi/core-legacy';
import { ToastService } from '@isophi/core-ui';
import { PhoneVerificationService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { interval, Subscription } from 'rxjs';
import { finalize, take, takeWhile } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { RouterLinkFactory } from '../../../core/router/router-link.factory';

const VERIFICATION_ERROR_CODES = {
  previous_verification_pending: 2401,
  invalid_or_expired: 2402,
  already_verified: 2403,
};

@Component({
  selector: 'app-verify-phone',
  templateUrl: './verify-phone.component.html',
  styleUrls: ['./verify-phone.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class VerifyPhoneComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly phoneVerificationService = inject(PhoneVerificationService);
  private readonly authService = inject(AuthService);
  private readonly toastService = inject(ToastService);
  private readonly translocoService = inject(TranslocoService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly links = inject(RouterLinkFactory);

  private countdownSubscription?: Subscription;
  private readonly resendCooldownDuration = 60;

  isProduction = environment.production;
  user: User | null = null;
  verificationSent = false;
  isSendingCode = false;
  isVerifying = false;
  resendCooldownSeconds = 0;

  verificationForm = this.formBuilder.group({
    phoneNumber: this.formBuilder.control('', [Validators.required]),
    code: this.formBuilder.control('', [
      Validators.required,
      Validators.pattern(/^\d{6}$/), // Exactly 6 digits
      Validators.minLength(6),
      Validators.maxLength(6),
    ]),
  });

  ngOnInit(): void {
    this.user = this.authService.loggedUser;

    if (!this.user) {
      this.links.login();
      return;
    }
    this.verificationForm.controls.phoneNumber.patchValue(this.authService.loggedUser.phone);
  }

  ngOnDestroy(): void {
    this.stopCooldownTimer();
  }

  onSendVerificationCode(phone: string): void {
    if (!phone || !phone.trim()) {
      this.toastService.error(this.translocoService.translate('verification/phoneRequired'));
      return;
    }

    this.isSendingCode = true;
    this.cdr.markForCheck();

    this.phoneVerificationService
      .requestUserPhoneVerification({
        phone: phone,
      })
      .pipe(
        take(1),
        finalize(() => {
          this.isSendingCode = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.verificationSent = true;
          this.toastService.success(this.translocoService.translate('verification/codeSent'));
          this.startCooldownTimer();
        },
        error: (error) => {
          console.error('Phone verification request failed:', error);

          // Handle specific error codes
          const errorData = error?.error;
          let errorMessage = this.translocoService.translate('verification/sendCodeFailed');

          if (errorData?.errors?.length > 0) {
            const errorCode = +errorData.errors[0].code;

            if (errorCode === VERIFICATION_ERROR_CODES.previous_verification_pending) {
              errorMessage = this.translocoService.translate('verification/phone/previousVerificationPending');
            } else if (errorCode === VERIFICATION_ERROR_CODES.invalid_or_expired) {
              errorMessage = this.translocoService.translate('verification/phone/invalidOrExpired');
            } else if (errorCode === VERIFICATION_ERROR_CODES.already_verified) {
              errorMessage = this.translocoService.translate('verification/phone/alreadyVerified');
            } else if (errorData.errors[0].message) {
              errorMessage = errorData.errors[0].message;
            }
          }

          this.toastService.error(errorMessage);
        },
      });
  }

  onVerifyCode(): void {
    if (this.verificationForm.invalid) {
      return;
    }

    const code = this.verificationForm.value.code;
    if (!code) {
      return;
    }

    this.isVerifying = true;
    this.cdr.markForCheck();

    this.phoneVerificationService
      .verifyUserPhone({
        code: code,
      })
      .pipe(
        take(1),
        finalize(() => {
          this.isVerifying = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: () => {
          this.toastService.success(this.translocoService.translate('verification/verificationSuccess'));
          this.authService.updateUser({ isPhonelVerified: true });
          this.navigateToNextPage();
        },
        error: (error) => {
          console.error('Phone verification failed:', error);

          // Handle specific error codes
          const errorData = error?.error;
          let errorMessage = this.translocoService.translate('verification/verificationFailed');

          if (errorData?.errors?.length > 0) {
            const errorCode = +errorData.errors[0].code;

            if (errorCode === VERIFICATION_ERROR_CODES.previous_verification_pending) {
              errorMessage = this.translocoService.translate('verification/phone/previousVerificationPending');
            } else if (errorCode === VERIFICATION_ERROR_CODES.invalid_or_expired) {
              errorMessage = this.translocoService.translate('verification/phone/invalidOrExpired');
            } else if (errorCode === VERIFICATION_ERROR_CODES.already_verified) {
              errorMessage = this.translocoService.translate('verification/phone/alreadyVerified');
            } else if (errorData.errors[0].message) {
              errorMessage = errorData.errors[0].message;
            }
          }

          this.toastService.error(errorMessage);
        },
      });
  }

  onResendCode(): void {
    if (!this.verificationForm.value.phoneNumber || !this.canResend) {
      return;
    }

    // this.verificationForm.reset();
    this.onSendVerificationCode(this.verificationForm.value.phoneNumber);
  }

  private startCooldownTimer(): void {
    this.stopCooldownTimer();
    this.resendCooldownSeconds = this.resendCooldownDuration;
    this.cdr.markForCheck();

    this.countdownSubscription = interval(1000)
      .pipe(takeWhile(() => this.resendCooldownSeconds > 0))
      .subscribe(() => {
        this.resendCooldownSeconds--;
        this.cdr.markForCheck();

        if (this.resendCooldownSeconds === 0) {
          this.stopCooldownTimer();
        }
      });
  }

  private stopCooldownTimer(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
  }

  get canResend(): boolean {
    return !this.isSendingCode && this.resendCooldownSeconds === 0;
  }

  navigateToNextPage(): void {
    this.router.navigate(this.links.help());
  }

  stepBack() {
    this.verificationSent = false;
  }
}
